/**
 * React Flow node type definitions
 * These types represent the modern React Flow format used in Admin3
 */
import { Edge, Node } from '@xyflow/react'

/**
 * Node Types Enum
 */
export enum NodeType {
  Transfer = 'transfer',
  TransferAndContinue = 'transfer_and_continue',
  SmsNotification = 'sms_notification',
  Webhook = 'webhook',
  InteractiveWebhook = 'interactive_webhook',
  CallDistribution = 'call_distribution',
  AutoAttendant = 'auto_attendant',
  ExternalTransfer = 'external_transfer',
  LeaveVoicemail = 'leave_voicemail',
  Schedule = 'schedule',
  DayNight = 'day_night',
  DialByName = 'dial_by_name',
  SoundClip = 'sound_clip',
  FaxDetect = 'fax_detect',
  Wait = 'wait',
  HangUp = 'hang_up',
  JumpToSwitchboard = 'jump_to_switchboard',
  ConferenceRoom = 'conference_room',
  Queue = 'queue',
  TransferAndRollover = 'transfer_and_rollover',
  ChangeCallerId = 'change_caller_id',
  AiAgentV1 = 'ai_agent_v1',
}

/**
 * React Flow Data Structure (stored in json_data)
 */
export interface SwitchboardFlowData {
  nodes: SwitchboardNode[]
  edges: SwitchboardEdge[]
}

/**
 * Extended Node with our custom data
 */
export interface SwitchboardNode extends Omit<Node, 'data' | 'type'> {
  type: NodeType | 'start'
  data: NodeData & Record<string, unknown>
}

/**
 * Extended Edge
 */
export type SwitchboardEdge = Edge

/**
 * Node Data Types
 */
export type NodeData =
  | TransferNodeData
  | QueueNodeData
  | WebhookNodeData
  | AutoAttendantNodeData
  | SmsNotificationNodeData
  | HangUpNodeData
  | WaitNodeData
  | ExternalTransferNodeData
  | LeaveVoicemailNodeData
  | ScheduleNodeData
  | DayNightNodeData
  | DialByNameNodeData
  | SoundClipNodeData
  | FaxDetectNodeData
  | JumpToSwitchboardNodeData
  | ConferenceRoomNodeData
  | TransferAndRolloverNodeData
  | ChangeCallerIdNodeData
  | CallDistributionNodeData
  | InteractiveWebhookNodeData

/**
 * Individual node data interfaces
 */

export interface TransferNodeData {
  extensionId?: string
  timeout?: number
  onNoAnswer?: 'continue' | 'voicemail' | 'hangup'
  onInvalid?: string // Node ID for invalid extension path
  onTimeout?: string // Node ID for timeout path
  [key: string]: unknown
}

export interface QueueNodeData {
  queueId?: string // Only field that exists in legacy HTML

  // Admin3-only fields (for UI functionality, not saved to legacy)
  // These should be excluded from legacy conversion via shouldSkipField
  maxWaitTime?: number
  queueMusic?: string

  // Connection metadata (stored in endpoints, not HTML)
  // These should be excluded from HTML conversion but used for endpoint conversion
  onTimeout?: string // Node ID for "On Timeout" handle (from React Flow edges)
  onTimeoutTarget?: string // Legacy format (deprecated - use onTimeout)
  onEscape?: string // Node ID for "On Escape" handle (from React Flow edges)
  [key: string]: unknown
}

export interface WebhookNodeData {
  url?: string
  method?: 'GET' | 'POST'
  headers?: Record<string, string>
  timeout?: number
  onSuccess?: string // next node id
  onFailure?: string // next node id
  [key: string]: unknown
}

export interface AutoAttendantNodeData {
  soundClipId?: string // Sound clip ID for greeting
  soundClipUuid?: string // Canonical sound clip UUID for greeting
  timeout?: number // Timeout in seconds
  repeatTimes?: number // Number of times to repeat
  allowExtensionDialing?: boolean // Allow extension dialing
  options?: AutoAttendantOption[] // List of digit options
  onInvalid?: string // Node ID for invalid extension path
  onTimeout?: string // Node ID for timeout path
  [key: string]: unknown
}

export interface AutoAttendantOption {
  digit: string // 0-9, *, #
  action: 'transfer' | 'voicemail' | 'hangup' | 'repeat' | 'next_node'
  target?: string // extension or node id
  id?: string // Option ID from legacy format (e.g., "1234567890123_option-9876543210123_0")
}

export interface SmsNotificationNodeData {
  messageType?: string // e.g., "Missed call"
  phoneNumber?: string // SMS recipient phone number
  message?: string // Message content
  sendFrom?: string // Messaging group ID or phone number to send from
  autoReplyToCaller?: boolean // Whether to auto-reply to caller
  minimized?: boolean // Minimize/maximize state (stored as "1"/"0" in legacy)
  validationErrors?: Record<string, string> // Field-level validation errors from save-time validation
  [key: string]: unknown
}

export interface HangUpNodeData {
  label?: string
  [key: string]: unknown
}

export interface WaitNodeData {
  duration?: number // seconds
  ringDuringWait?: boolean // Play ring tone during wait
  [key: string]: unknown
}

export interface ExternalTransferNodeData {
  phoneNumber?: string // External phone number to transfer to
  timeout?: number // Timeout in seconds
  sipServer?: string // SIP server address
  passThroughCallerId?: boolean // Pass through caller ID
  phoneNumberId?: string // Phone number ID from dropdown (if not passing through caller ID) - stored as phone_number_id in legacy
  callerId?: string // Deprecated: kept for backward compatibility, use phoneNumberId instead
  transferCompany?: string // Selected company from prepopulated dropdown - stored as transfer_company in legacy
  requireOneToConnect?: boolean // Require 1 to connect
  minimized?: boolean // Minimize/maximize state (stored as "1"/"0" in legacy)
  validationErrors?: Record<string, string> // Field-level validation errors from save-time validation
  [key: string]: unknown
}

export interface LeaveVoicemailNodeData {
  extensionId?: string // Extension ID for voicemail
  soundClipId?: string // Sound clip ID for voicemail greeting
  soundClipUuid?: string // Canonical sound clip UUID for voicemail greeting
  [key: string]: unknown
}

export interface ScheduleNodeData {
  scheduleId?: string // Selected schedule ID
  scheduleUuid?: string // Canonical schedule UUID for the selected legacy ID
  businessHours?: string
  holidays?: string[]
  timezone?: string
  onBusinessHours?: string // node id (legacy)
  onAfterHours?: string // node id (legacy)
  whenOpen?: string // Node ID for "When Open" handle
  whenClosed?: string // Node ID for "When Closed" handle
  holiday?: string // Node ID for "Holiday" handle
  [key: string]: unknown
}

export interface DayNightNodeData {
  dayPath?: string // node id (legacy)
  nightPath?: string // node id (legacy)
  toggleCode?: string | null // Toggle code (e.g., "*30", "*31", etc.) or null for Advanced mode (uses *3)
  status?: 'open' | 'closed' // Current status (OPEN/CLOSED)
  whenOpen?: string // Node ID for "When Open" handle
  whenClosed?: string // Node ID for "When Closed" handle
  [key: string]: unknown
}

export interface DialByNameNodeData {
  directoryExtensionId?: string
  lookupId?: 'extension_name' | 'username' // Lookup By: Extension Name or User Name
  sortOrder?: 'first_name' | 'last_name' | 'first_last_name' // Sort order for displaying names
  [key: string]: unknown
}

export interface SoundClipNodeData {
  soundClipId?: string
  soundClipUuid?: string
  [key: string]: unknown
}

export interface FaxDetectNodeData {
  faxExtensionId?: string // Fax extension ID (Virtual Fax Machine extension)
  faxDestination?: string // Legacy field name
  voicePath?: string // node id
  [key: string]: unknown
}

export interface JumpToSwitchboardNodeData {
  targetSwitchboardId?: string // Switchboard extension ID
  [key: string]: unknown
}

export interface ConferenceRoomNodeData {
  conferenceRoomId?: string
  [key: string]: unknown
}

export interface TransferAndRolloverNodeData {
  extensions?: string[]
  timeout?: number
  noAnswer?: string // Node ID for "No Answer" handle
  busy?: string // Node ID for "Busy" handle
  [key: string]: unknown
}

export interface AiAgentV1NodeData {
  agentId?: string | null // UUID string (36 characters) or null
  validationErrors?: Record<string, string> // Field-level validation errors from save-time validation
  [key: string]: unknown
}

export interface ChangeCallerIdNodeData {
  newCallerId?: string // The text input value
  mode?: 'replace' | 'add_front' // Radio button selection: 'replace' or 'add_front'
  minimized?: boolean // Minimize/maximize state (stored as "1"/"0" in legacy)
  validationErrors?: Record<string, string> // Field-level validation errors from save-time validation
  [key: string]: unknown
}

export interface CallDistributionOption {
  weight?: number // Weight percentage (0-100)
  id?: string // Option ID from legacy format (e.g., "1234567890123_option-9876543210123_0")
}

export interface CallDistributionNodeData {
  timeout?: number // Timeout in seconds
  options?: CallDistributionOption[] // List of weight options
  [key: string]: unknown
}

export interface InteractiveWebhookOption {
  value: string // Option value (e.g., "1", "2", "3")
  label?: string // Optional label for the option
  id?: string // Option ID from legacy format (e.g., "1234567890123_option-9876543210123_0")
}

export interface InteractiveWebhookNodeData {
  url?: string
  method?: 'GET' | 'POST'
  allowExtensionAsOption?: boolean // Allow extension as option checkbox
  options?: InteractiveWebhookOption[] // List of options/children
  children?: InteractiveWebhookOption[] // Legacy field name
  inputCollection?: Record<string, string> // Legacy field
  responseHandling?: string
  onInvalid?: string // Node ID for invalid path
  onTimeout?: string // Node ID for timeout path
  [key: string]: unknown
}

/**
 * Type guards
 */

export function isSwitchboardNode(node: Node): node is SwitchboardNode {
  return 'type' in node && 'data' in node
}

export function isNodeType(
  node: SwitchboardNode,
  type: NodeType | 'start',
): boolean {
  return node.type === type
}
